import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * ToastService for the mobile app — wraps Ionic ToastController.
 * Provides consistent user feedback for API errors, sync status, and actions.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastCtrl = inject(ToastController);

  async success(message: string): Promise<void> {
    await this.show(message, 'success');
  }

  async error(message: string, onRetry?: () => void): Promise<void> {
    await this.show(message, 'error', onRetry);
  }

  async warning(message: string): Promise<void> {
    await this.show(message, 'warning');
  }

  async info(message: string): Promise<void> {
    await this.show(message, 'info');
  }

  private async show(message: string, type: ToastType, onRetry?: () => void): Promise<void> {
    const colorMap: Record<ToastType, string> = {
      success: 'success',
      error: 'danger',
      warning: 'warning',
      info: 'primary',
    };

    const buttons: { text: string; role?: string; handler?: () => void }[] = [];

    if (type === 'error' && onRetry) {
      buttons.push({
        text: 'Reintentar',
        handler: () => onRetry(),
      });
      buttons.push({ text: 'Cerrar', role: 'cancel' });
    } else if (type === 'error') {
      buttons.push({ text: 'OK', role: 'cancel' });
    }

    const toast = await this.toastCtrl.create({
      message,
      duration: type === 'error' ? (onRetry ? 8000 : 5000) : 3000,
      position: 'bottom',
      color: colorMap[type],
      buttons: buttons.length > 0 ? buttons : undefined,
    });

    await toast.present();
  }
}
